import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWalletStore } from '@/stores/wallet';
import { useMultisigStore } from '@/stores/multisig';
import { getConfig } from '@/lib/config';
import { signedFetch } from '@/lib/signedFetch';
import { createMultisig } from '@/lib/createMultisig';
import { env } from '@/lib/env';
import WalletPicker from '@/components/WalletPicker';
import TotpActivate from '@/components/TotpActivate';
import TotpRecoveryCodes from '@/components/TotpRecoveryCodes';

type Step =
  | 'wallet'
  | 'create' // 2a — on-chain MultisigCreateV2 (costs SOL)
  | 'register' // 2b — backend finalize + TOTP setup (free, retry-safe)
  | 'totp'
  | 'verify'
  | 'recovery'
  | 'done';

function shortAddr(addr: string) {
  return addr.slice(0, 4) + '…' + addr.slice(-4);
}

export default function Setup() {
  const navigate = useNavigate();

  const walletPubkey = useWalletStore((s) => s.pubkey);
  const walletPublicKey = useWalletStore((s) => s.publicKey);
  const isConnected = walletPubkey !== null;

  const list = useMultisigStore((s) => s.list);
  const addMultisig = useMultisigStore((s) => s.add);
  const markFinalized = useMultisigStore((s) => s.markFinalized);

  const [step, setStep] = useState<Step>('wallet');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [openPicker, setOpenPicker] = useState(false);

  const [pendingMultisigPda, setPendingMultisigPda] = useState<string | null>(null);
  const [pendingCreateKey, setPendingCreateKey] = useState<string | null>(null);
  const [pendingCreateSig, setPendingCreateSig] = useState<string | null>(null);

  const [totpUri, setTotpUri] = useState<string | null>(null);
  const [totpSecret, setTotpSecret] = useState<string | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);

  useEffect(() => {
    if (step !== 'wallet') return;
    if (!isConnected) return;
    // Resume: if the connected wallet already created a multisig but didn't
    // finish backend registration, jump straight to step 2b.
    const owner = walletPubkey;
    if (owner) {
      const pending = list.find((m) => m.ownerPubkey === owner && !m.finalized);
      if (pending) {
        setPendingMultisigPda(pending.multisigPda);
        setPendingCreateKey(pending.createKey);
        setPendingCreateSig(pending.createSig ?? null);
        setStep('register');
        return;
      }
    }
    setStep('create');
  }, [step, isConnected, walletPubkey, list]);

  async function doCreate() {
    const pk = walletPublicKey();
    if (!pk) return;
    setBusy(true);
    setError(null);
    try {
      const cfg = await getConfig();
      const result = await createMultisig({
        rpcUrl: env.solanaRpcUrl,
        programId: cfg.programId,
        backendSignerPubkey: cfg.activeBackendSignerPubkey,
      });
      setPendingMultisigPda(result.multisigPda);
      setPendingCreateKey(result.createKey);
      setPendingCreateSig(result.signature);
      addMultisig({
        multisigPda: result.multisigPda,
        createKey: result.createKey,
        ownerPubkey: pk.toBase58(),
        createSig: result.signature,
        finalized: false,
      });
      setStep('register');
    } catch (err) {
      console.error(err);
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function doRegister() {
    if (!pendingMultisigPda || !pendingCreateKey) {
      setError('No pending multisig — go back and create one first.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const finalize = await signedFetch('multisig.finalize', {
        multisigPda: '-',
        fields: {
          'Multisig Pda': pendingMultisigPda,
          'Create Key': pendingCreateKey,
        },
        ttlSec: 120,
      });
      if (!finalize.ok) {
        throw new Error(
          `finalize failed: ${
            (finalize.data as { message?: string })?.message ?? finalize.status
          }`,
        );
      }
      markFinalized(pendingMultisigPda);

      const setup = await signedFetch<{
        uri: string;
        secret: string;
        recoveryCodes: string[];
      }>('mfa.totp.setup');
      if (!setup.ok) throw new Error(`mfa setup failed: ${setup.status}`);
      setTotpUri(setup.data.uri);
      setTotpSecret(setup.data.secret);
      setRecoveryCodes(setup.data.recoveryCodes);
      setStep('totp');
    } catch (err) {
      console.error(err);
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function onVerify(code: string) {
    setBusy(true);
    setError(null);
    try {
      const res = await signedFetch('mfa.totp.verify', { fields: { Code: code } });
      if (!res.ok) {
        throw new Error(
          `verify failed: ${
            (res.data as { message?: string })?.message ?? res.status
          }`,
        );
      }
      setStep('recovery');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  function finishSetup() {
    setStep('done');
    setTimeout(() => navigate('/app'), 1500);
  }

  return (
    <div className="max-w-xl mx-auto">
      <h1 className="font-display text-2xl font-semibold mb-2">
        Set up your 2FA wallet
      </h1>
      <p className="text-sm text-ink-300 mb-8">
        Five steps. One on-chain transaction. About 90 seconds.
      </p>

      <div className="rounded-xl border border-ink-800 bg-ink-900/40 p-6 space-y-6">
        <ol className="flex items-center gap-2 text-xs text-ink-400 mb-4">
          <li className={step === 'wallet' ? 'text-avo-400' : 'text-ink-300'}>
            1. Wallet
          </li>
          <li>›</li>
          <li
            className={
              ['create', 'register'].includes(step)
                ? 'text-avo-400'
                : step === 'wallet'
                  ? 'text-ink-500'
                  : 'text-ink-300'
            }
          >
            2. Multisig
          </li>
          <li>›</li>
          <li
            className={
              ['totp', 'verify'].includes(step)
                ? 'text-avo-400'
                : ['wallet', 'create', 'register'].includes(step)
                  ? 'text-ink-500'
                  : 'text-ink-300'
            }
          >
            3. TOTP
          </li>
          <li>›</li>
          <li
            className={
              step === 'recovery'
                ? 'text-avo-400'
                : step === 'done'
                  ? 'text-ink-300'
                  : 'text-ink-500'
            }
          >
            4. Recovery
          </li>
          <li>›</li>
          <li className={step === 'done' ? 'text-avo-400' : 'text-ink-500'}>
            5. Done
          </li>
        </ol>

        {step === 'wallet' && (
          <div className="space-y-4">
            <p className="text-sm text-ink-300">
              Connect any Solana wallet — Phantom, Solflare, Backpack and other
              Wallet Standard wallets work.
            </p>
            <button
              type="button"
              onClick={() => setOpenPicker(true)}
              className="w-full rounded-md bg-avo-500 px-4 py-3 text-sm font-semibold text-black hover:bg-avo-600 transition-colors"
            >
              Connect Wallet
            </button>
            {openPicker && (
              <WalletPicker onClose={() => setOpenPicker(false)} />
            )}
          </div>
        )}

        {step === 'create' && (
          <div>
            <h2 className="text-base font-semibold text-ink-50 mb-2">
              Create your 2-of-2 multisig
            </h2>
            <ul className="text-sm text-ink-300 space-y-1 mb-4 list-disc list-inside">
              <li>
                Member 1: your wallet ({shortAddr(walletPubkey ?? '')}) — full
                permissions
              </li>
              <li>Member 2: Avocado backend signer — Vote-only</li>
              <li>Threshold: 2 — both signatures required to move funds</li>
            </ul>
            <p className="text-xs text-ink-500 mb-4">
              On-chain rent + fees ≈ 0.0023 SOL. One signature in your wallet.
            </p>
            {error && <p className="text-sm text-red-400 mb-3">{error}</p>}
            <button
              type="button"
              onClick={() => void doCreate()}
              disabled={busy}
              className="w-full rounded-lg bg-avo-500 px-4 py-3 text-sm font-medium text-ink-950 hover:bg-avo-400 transition-colors disabled:opacity-50"
            >
              {busy ? 'Creating on-chain…' : 'Create multisig'}
            </button>
          </div>
        )}

        {step === 'register' && (
          <div>
            <div className="rounded-lg border border-avo-500/30 bg-avo-500/5 p-3 mb-4">
              <p className="text-xs text-avo-300 font-medium mb-1">
                ✓ Multisig created on-chain
              </p>
              <p className="text-xs text-ink-400 font-mono break-all">
                {pendingMultisigPda}
              </p>
              {pendingCreateSig && (
                <a
                  href={`https://orbmarkets.io/tx/${pendingCreateSig}`}
                  target="_blank"
                  rel="noopener"
                  className="text-xs text-avo-400 hover:text-avo-300 mt-1 inline-block"
                >
                  View on Orb ↗
                </a>
              )}
            </div>
            <h2 className="text-base font-semibold text-ink-50 mb-2">
              Register with the Avocado backend
            </h2>
            <p className="text-sm text-ink-300 mb-4">
              Two signatures in your wallet — no on-chain transaction. The
              backend records your multisig and issues your TOTP secret +
              recovery codes.
            </p>
            {error && <p className="text-sm text-red-400 mb-3">{error}</p>}
            <button
              type="button"
              onClick={() => void doRegister()}
              disabled={busy}
              className="w-full rounded-lg bg-avo-500 px-4 py-3 text-sm font-medium text-ink-950 hover:bg-avo-400 transition-colors disabled:opacity-50"
            >
              {busy ? 'Registering…' : 'Register with backend'}
            </button>
            <p className="text-[11px] text-ink-500 mt-3 text-center">
              If this fails, you can retry — it never re-creates the on-chain
              multisig.
            </p>
          </div>
        )}

        {step === 'totp' && totpUri && totpSecret && (
          <div>
            <h2 className="text-base font-semibold text-ink-50 mb-2">
              Scan with your authenticator
            </h2>
            <TotpActivate
              uri={totpUri}
              secret={totpSecret}
              onVerify={(code) => void onVerify(code)}
            />
            {error && <p className="text-sm text-red-400 mt-2">{error}</p>}
          </div>
        )}

        {step === 'recovery' && (
          <div>
            <h2 className="text-base font-semibold text-ink-50 mb-2">
              Save your recovery codes
            </h2>
            <TotpRecoveryCodes codes={recoveryCodes} onContinue={finishSetup} />
          </div>
        )}

        {step === 'done' && (
          <div className="text-center py-6">
            <div className="w-12 h-12 rounded-full bg-avo-500/20 text-avo-400 grid place-content-center mx-auto mb-3 text-2xl">
              ✓
            </div>
            <h2 className="text-lg font-semibold text-ink-50">
              Set up complete.
            </h2>
            <p className="text-sm text-ink-300 mt-1">
              Redirecting to your dashboard…
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
